import { type KeptClip, type KeptClipStore, type KeptReason, keptClipFileName } from './kept-clips';

/**
 * "Dictados sin transcribir" under the input: one row per kept clip with Reintentar, Descargar audio
 * and Descartar (two clicks, so a stray tap cannot throw a dictation away). Hidden while empty.
 */

export const KEPT_TEXT = {
  title: 'Dictados sin transcribir',
  hint: 'El audio quedó guardado en este equipo.',
  retry: 'Reintentar',
  download: 'Descargar audio',
  discard: 'Descartar',
  confirmDiscard: 'Confirmar descarte',
  reasons: {
    failed: 'falló la transcripción',
    too_large: 'demasiado grande para transcribir',
    interrupted: 'grabación interrumpida',
  } satisfies Record<KeptReason, string>,
} as const;

/** How long "Confirmar descarte" waits for the second click. */
const CONFIRM_DISCARD_MS = 4_000;
const REVOKE_URL_MS = 60_000;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', `assistant-btn ${className}`, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

/** Saves the clip as a file through a temporary object URL. */
export function downloadClip(clip: KeptClip): void {
  const url = URL.createObjectURL(clip.audio);
  const link = el('a', 'assistant-kept-link');
  link.href = url;
  link.download = keptClipFileName(clip);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked once the browser has surely started the download (one timer).
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_URL_MS);
}

function timeLabel(clip: KeptClip): string {
  const d = new Date(clip.createdAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export class KeptClipsView {
  readonly element = el('section', 'assistant-kept');
  private readonly rows = el('ul', 'assistant-kept-list');
  private busy = false;

  constructor(private readonly store: KeptClipStore, private readonly onRetry: (clip: KeptClip) => Promise<void>) {
    this.element.setAttribute('aria-label', KEPT_TEXT.title);
    this.element.append(el('strong', 'assistant-kept-title', KEPT_TEXT.title), el('p', 'assistant-kept-hint', KEPT_TEXT.hint), this.rows);
    store.onChange(() => this.render());
    this.render();
  }

  /** Retries wait while a recording or another transcription runs. */
  setBusy(busy: boolean): void {
    this.busy = busy;
    this.rows.querySelectorAll<HTMLButtonElement>('.assistant-kept-retry').forEach((b) => { b.disabled = busy; });
  }

  private render(): void {
    const clips = this.store.list();
    this.element.hidden = clips.length === 0;
    this.rows.replaceChildren(...clips.map((clip) => this.row(clip)));
  }

  private row(clip: KeptClip): HTMLLIElement {
    const li = el('li', 'assistant-kept-row');
    li.dataset.clip = clip.id;
    li.append(el('span', 'assistant-kept-label', `${timeLabel(clip)} · ${KEPT_TEXT.reasons[clip.reason]}`));
    const retry = button(KEPT_TEXT.retry, 'assistant-kept-retry', () => void this.retry(clip));
    retry.disabled = this.busy;
    const discard = button(KEPT_TEXT.discard, 'assistant-kept-discard', () => this.discard(clip, discard));
    li.append(retry, button(KEPT_TEXT.download, 'assistant-kept-download', () => downloadClip(clip)), discard);
    return li;
  }

  private async retry(clip: KeptClip): Promise<void> {
    if (this.busy) return;
    this.setBusy(true);
    try {
      await this.onRetry(clip);
    } finally {
      this.setBusy(false);
    }
  }

  private discard(clip: KeptClip, b: HTMLButtonElement): void {
    if (b.dataset.confirm !== 'true') {
      b.dataset.confirm = 'true';
      b.textContent = KEPT_TEXT.confirmDiscard;
      setTimeout(() => {
        b.dataset.confirm = '';
        b.textContent = KEPT_TEXT.discard;
      }, CONFIRM_DISCARD_MS);
      return;
    }
    void this.store.remove(clip.id);
  }
}
